// React hooks
import { useState } from "react";

// MUI components
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Alert,
  Typography,
} from "@mui/material";

// MUI icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";

// Project components
import CancelConfirm from "../CancelConfirm/CancelConfirm";
import ProcessingDialog from "../ProcessingDialog/ProcessingDialog";
import ErrorDialog from "../ErrorDialog/ErrorDialog";

// Project styles
import alert_styles from "../Alert/alert_styles";
import card_styles, { accordion_styles } from "./card_styles";

// Project services/helpers
import {
  adjustDateIfInTerm,
  format_date,
  format_price,
  getFormattedCancelDate,
} from "../../../../../services/global.services";
import { cancel_agreement } from "../../../../../services/api.calls.services";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../services/StoreHelper";
import { update_state_value } from "../../../../../store/reducers/AppReducer";

interface Contract {
  agreementId: string;
  name: string;
  agreedDateUtc: string;
  nextPaymentDueDateUtc?: any;
  nextPaymentAmount?: number;
  nextPaymentTaxAmount?: number;
  isAutoRenewed: boolean;
  cancellationFee: number;
  onlineCancellationDetail?: string;
  installmentsRemaining?: number;
  cancelDateUtc?: string | null;
  scheduledForCancellingOnUtc?: string | null;
  agreement_processing?: boolean;
  delinquentDate?: string | null;
  canCancelOnline: boolean;
  lastInstallmentDueDateUtc: any;
  frozen: boolean;
  noticePeriodDays: number;
  onlineCancelUrl?: string;
  onlineCancelMessage?: string;
  excludeOnlineCancelIfPurchasedInPerson?: boolean;
  onlineCancelReview: boolean;
  cancellationPendingReview: boolean;
}

interface CardProps {
  contract: Contract;
  refresh: any;
}

function Card({ contract, refresh }: CardProps) {
  const { temp_token, user, club } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();

  // 🔹 simple toggle (same as other files)
  const { usesSpanish } = club;

  const [expanded, setExpanded] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showProcessingCancelConfirm, setShowProcessingCancelConfirm] =
    useState(false);
  const [showError, setShowError] = useState(false);
  const [inTerm, setInTerm] = useState(
    contract.installmentsRemaining ? "in-term" : "out-of-term"
  );
  const [note, setNote] = useState("");

  const setCancellationNote = (note: string) => setNote(note);

  const toggle_cancel_confirm_dialog = (open = false) =>
    setShowCancelConfirm(open);

  const toggle_processing_cancel_dialog = async (open = false) => {
    dispatch(update_state_value({ key: "cancel_process", value: true }));
    contract = JSON.parse(JSON.stringify(contract));

    const isInTerm = !!contract.installmentsRemaining;

    if (isInTerm) {
      setInTerm("in-term");
      contract.agreement_processing = true;
    } else {
      setInTerm("out-of-term");
    }

    setShowCancelConfirm(false);

    const userToken = JSON.parse(localStorage.getItem("user_token") || "null");
    const authToken = userToken
      ? userToken.expires_in
        ? userToken.access_token
        : userToken.refresh_token
      : temp_token;

    const noticePeriodDays =
      !isInTerm && contract.noticePeriodDays ? contract.noticePeriodDays : 0;

    const cancelDate: any = getFormattedCancelDate(
      contract,
      true,
      noticePeriodDays
    );

    cancel_agreement(
      authToken,
      contract.agreementId,
      cancelDate,
      note,
      isInTerm,
      user.UserID
    )
      .then(() => {
        setShowProcessingCancelConfirm(open);
        let url = club.onlineCancelUrl;
        if (url) {
          if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
          window.location.replace(url);
        }
      })
      .catch(() => setShowError(open));

    dispatch(update_state_value({ key: "cancel_process", value: false }));
  };

  const get_button = () => {
    if (
      (contract.agreement_processing ||
        contract.scheduledForCancellingOnUtc ||
        contract.cancelDateUtc ||
        contract.cancellationPendingReview) &&
      !contract.frozen
    )
      return <div />;

    const isDisabled =
      !contract.canCancelOnline ||
      (!contract.installmentsRemaining && !contract.isAutoRenewed) ||
      contract.frozen ||
      contract.excludeOnlineCancelIfPurchasedInPerson;

    return (
      <Button
        style={
          isDisabled
            ? card_styles.cancel_button_disabled
            : card_styles.cancel_button
        }
        onClick={() => toggle_cancel_confirm_dialog(true)}
        disabled={isDisabled}
      >
        {usesSpanish ? "CANCELAR" : "CANCEL"}
      </Button>
    );
  };

  const get_status_text = () => {
    if (contract.cancelDateUtc && !contract.frozen) {
      return usesSpanish
        ? `Cancelado el ${format_date(contract.cancelDateUtc)}`
        : `Cancelled On ${format_date(contract.cancelDateUtc)}`;
    }

    if (contract.frozen)
      return usesSpanish ? "Activo - Congelado" : "Active - Frozen";

    if (contract.cancellationPendingReview)
      return usesSpanish
        ? "Activo - Cancelación solicitada"
        : "Active - Cancel Requested";

    if (contract.scheduledForCancellingOnUtc)
      return usesSpanish
        ? "Activo - Por cancelar"
        : "Active - To Be Cancelled";

    if (!contract.installmentsRemaining && !contract.isAutoRenewed)
      return usesSpanish
        ? "Activo - Pagado por completo"
        : "Active - Fully Paid";

    return usesSpanish
      ? `Activo - Plazo ${inTerm === "in-term" ? "No " : ""}cumplido`
      : `Active - Term ${inTerm === "in-term" ? "Not " : ""}Fulfilled`;
  };

  return (
    <Accordion
      style={card_styles.accordion}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      square
      sx={accordion_styles}
    >
      <AccordionSummary style={card_styles.accordion_summary}>
        <div style={card_styles.contents}>
          <div style={card_styles.left_col}>
            <span style={card_styles.left_col_text}>{contract.name}</span>
          </div>

          <div style={card_styles.right_col}>
            <span style={card_styles.next_pay}>
              {contract.nextPaymentDueDateUtc ? (
                <>
                  {usesSpanish ? "Próximo pago:" : "Next Payment:"}{" "}
                  <strong style={card_styles.next_pay_caption}>
                    {format_price(
                      (contract.nextPaymentAmount ?? 0) +
                        (contract.nextPaymentTaxAmount ?? 0)
                    )}
                  </strong>{" "}
                  {usesSpanish ? "el" : "on"}{" "}
                  <strong style={card_styles.next_pay_caption}>
                    {adjustDateIfInTerm(contract.nextPaymentDueDateUtc)}
                  </strong>
                </>
              ) : usesSpanish ? (
                "No hay pagos futuros"
              ) : (
                "No Future Payments"
              )}
            </span>

            <span style={card_styles.status_v2}>{get_status_text()}</span>
            <ExpandMoreIcon />
          </div>
        </div>
      </AccordionSummary>

      <AccordionDetails style={card_styles.details_container}>
        {get_button()}

        <Alert
          icon={<InfoOutlineIcon sx={{ fontSize: 20 }} />}
          severity="info"
          sx={alert_styles.container_custom}
        >
          <Typography sx={alert_styles.alert_text_custom}>
            {usesSpanish
              ? "Este acuerdo no se puede cancelar en línea. Comuníquese con el club."
              : "This agreement cannot be cancelled online. Please contact the club for further assistance."}
          </Typography>
        </Alert>

        <CancelConfirm
          contract={contract}
          open={showCancelConfirm}
          onClose={() => toggle_cancel_confirm_dialog(false)}
          onCancel={() => toggle_processing_cancel_dialog(true)}
          setNote={setCancellationNote}
          cancelDate={getFormattedCancelDate(
            contract,
            false,
            contract.noticePeriodDays
          )}
        />

        <ProcessingDialog
          message={
            usesSpanish
              ? "Tu acuerdo se ha cancelado correctamente."
              : "Your agreement has been successfully cancelled."
          }
          open={showProcessingCancelConfirm}
          onClose={() => setShowProcessingCancelConfirm(false)}
          inTerm={inTerm}
          onlineCancelReview={contract.onlineCancelReview}
        />

        <ErrorDialog
          error={
            usesSpanish
              ? "Ocurrió un error. Comuníquese con su club."
              : "An unknown error has occurred please contact your club."
          }
          open={showError}
          onClose={() => setShowError(false)}
          contract={contract}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default Card;